import type { ClientGPSLocation } from '@DigitaleDoerfer/digitale-doerfer-api';
import color from 'color';
import type { FC } from 'react';
import { useEffect } from 'react';

import { convertPosition } from '@/features/geo-area/utils/map';
import type {
  FitToViewMessageData,
  SetAnimateMessageData,
  SetCanMoveMessageData,
  SetCenterMessageData,
  SetMarkersMessageData,
  SetThemeMessageData
} from '@/features/geo-area/utils/mapMessages';
import { Events, MarkerType } from '@/features/geo-area/utils/mapMessages';
import { useEmbeddedWebpageContext } from '@/shared/components/embedded/useEmbeddedWebpageContext';
import { useAppTheme } from '@/styles/hooks/useAppTheme';

type Props = {
  position: ClientGPSLocation | undefined;
  addMarker: boolean;
  canSelect: boolean;
};

export const LocationPickerMapContent: FC<Props> = ({
  position,
  addMarker,
  canSelect
}) => {
  const { sendMessage } = useEmbeddedWebpageContext();
  const {
    colors: { tertiary }
  } = useAppTheme();

  useEffect(() => {
    sendMessage({
      event: Events.SET_ANIMATE,
      payload: {
        animate: false
      }
    } satisfies SetAnimateMessageData);
  }, [sendMessage, canSelect]);

  useEffect(() => {
    sendMessage({
      event: Events.SET_THEME,
      payload: {
        markerColor: tertiary,
        rangeColor: color(tertiary).alpha(0.3).string()
      }
    } satisfies SetThemeMessageData);
  }, [sendMessage, tertiary]);

  useEffect(() => {
    if (!position) return;
    sendMessage({
      event: Events.SET_CENTER,
      payload: {
        position: convertPosition(position)
      }
    } satisfies SetCenterMessageData);
    sendMessage({
      event: Events.FIT_TO_VIEW,
      payload: { center: true, markers: true, range: true, zoom: 17 }
    } satisfies FitToViewMessageData);
  }, [sendMessage, position]);

  useEffect(() => {
    sendMessage({
      event: Events.SET_CAN_MOVE,
      payload: {
        canMove: canSelect
      }
    } satisfies SetCanMoveMessageData);
  }, [sendMessage, canSelect]);

  useEffect(() => {
    sendMessage({
      event: Events.SET_MARKERS,
      payload: {
        markers:
          canSelect || !addMarker || !position
            ? []
            : [
                {
                  id: 'current',
                  type: MarkerType.PIN,
                  size: 40,
                  position: convertPosition(position)
                }
              ]
      }
    } satisfies SetMarkersMessageData);
    sendMessage({
      event: Events.FIT_TO_VIEW,
      payload: { center: true, markers: true, range: true, zoom: 17 }
    } satisfies FitToViewMessageData);
  }, [addMarker, canSelect, position, sendMessage]);

  return <></>;
};
